import axios from '../utils/axios';

// Helper to get current scoring mode from localStorage
const getScoringMode = () => localStorage.getItem('qoc_scoring_mode') || 'benchmark';

const QoCService = {
    // ==================== STANDARDS ====================
    getStandards(filters = {}) {
        return axios.get('qoc/standards', { params: filters });
    },
    saveStandard(data) {
        return axios.post('qoc/standards/save', data);
    },
    deleteStandard(id) {
        return axios.post('qoc/standards/delete', { id });
    },

    // ==================== CLAUSES ====================
    // FIXED: accepts params so standard_id filter works
    getClauses(params = {}) {
        return axios.get('qoc/clauses', { params });
    },
    saveClause(data) {
        return axios.post('qoc/clauses/save', data);
    },
    deleteClause(id) {
        return axios.post(`qoc/clauses/delete/${id}`);
    },

    // ==================== SUB-STANDARDS ====================
    getSubStandards(clauseIdOrFilters) {
        const params = typeof clauseIdOrFilters === 'object'
            ? clauseIdOrFilters
            : { clause_id: clauseIdOrFilters };
        return axios.get('qoc/sub-standards', { params });
    },
    saveSubStandard(data) {
        return axios.post('qoc/sub-standards/save', data);
    },
    deleteSubStandard(id) {
        return axios.post('qoc/sub-standards/delete', { id });
    },

    // ==================== QUESTIONS ====================
    getQuestions(subStandardId) {
        return axios.get('qoc/questions', { params: { sub_standard_id: subStandardId } });
    },
    saveBulkQuestions(data) {
        return axios.post('qoc/questions/bulk-save', data);
    },
    deleteQuestion(id) {
        return axios.post('qoc/questions/delete', { id });
    },

    // ==================== MEASURABLE ELEMENTS ====================
    getApplicableMEs(filters = {}) {
        return axios.get('qoc/applicable-mes', { params: filters });
    },
    getMeasurableElements(filters = {}) {
        return axios.get('qoc/measurable-elements', { params: filters });
    },
    saveMeasurableElement(data) {
        return axios.post('qoc/measurable-elements/save', data);
    },
    bulkSaveMeasurableElements(elements) {
        return axios.post('qoc/measurable-elements/bulk-save', { elements });
    },
    getRefNoComparison(ref_no, filters = {}) {
        return axios.get('qoc/measurable-elements/ref-comparison', {
            params: { ref_no, ...filters },
        });
    },

    // ==================== ASSESSMENTS ====================
    getAssessments(filters = {}) {
        return axios.get('qoc/assessments', { params: filters });
    },
    getAssessmentDetail(id) {
        return axios.get(`qoc/assessments/${id}`);
    },
    saveAssessment(data) {
        return axios.post('qoc/assessments/save', data);
    },
    submitAssessment(id) {
        return axios.post(`qoc/assessments/${id}/submit`);
    },
    approveAssessment(id) {
        return axios.post(`qoc/assessments/${id}/approve`);
    },
    rejectAssessment(id, rejectionReason) {
        return axios.post(`qoc/assessments/${id}/reject`, { rejection_reason: rejectionReason });
    },
    requestRevision(id, revisionNotes) {
        return axios.post(`qoc/assessments/${id}/request-revision`, { revision_notes: revisionNotes });
    },
    checkDuplicateAssessment(facilityId, assessmentDate, currentId = null) {
        return axios.get('qoc/assessments/check-duplicate', {
            params: { facility_id: facilityId, assessment_date: assessmentDate, current_id: currentId },
        });
    },

    // ==================== ME-BASED RESPONSES ====================
    getAssessmentResponsesME(assessmentId) {
        return axios.get(`qoc/assessments/${assessmentId}/responses-me`);
    },
    saveAssessmentResponsesME(assessmentId, responses) {
        return axios.post(`qoc/assessments/${assessmentId}/responses-me/save`, { responses });
    },

    // ==================== CLAUSE SCORES ====================
    getClauseScores(assessmentId) {
        return axios.get(`qoc/assessments/${assessmentId}/clause-scores`);
    },
    recalculateClauseScores(assessmentId) {
        return axios.post(`qoc/assessments/${assessmentId}/recalculate-scores`);
    },
    getClauseScoresAcrossFacilities(filters = {}) {
        return axios.get('qoc/clause-scores/across-facilities', { params: filters });
    },

    // ==================== ASSESSMENT PERIODS ====================
    getPeriods() {
        return axios.get('qoc/periods');
    },
    savePeriod(data) {
        return axios.post('qoc/periods/save', data);
    },
    deletePeriod(id) {
        return axios.post(`qoc/periods/${id}/delete`);
    },
    setCurrentPeriod(id) {
        return axios.post('qoc/periods/set-current', { id });
    },

    // ==================== AUDIT SCHEDULES ====================
    getAuditSchedules(filters = {}) {
        return axios.get('qoc/audit-schedules', { params: filters });
    },
    getSchedulesForAssessor(userId = null) {
        return axios.get('qoc/audit-schedules/for-assessor', {
            params: userId ? { user_id: userId } : {},
        });
    },
    bulkSaveSchedules(schedules) {
        return axios.post('qoc/audit-schedules/bulk-save', { schedules });
    },
    saveAssessmentForSchedule(data) {
        return axios.post('qoc/audit-schedules/save-assessment', data);
    },
    saveMigratedAssessment(data) {
        return axios.post('qoc/save-migrated-assessment', data);
    },
    saveAuditSchedule(data) {
        return axios.post('qoc/audit-schedules/save', data);
    },
    deleteAuditSchedule(id) {
        return axios.post(`qoc/audit-schedules/${id}/delete`);
    },

    // ==================== ANALYTICS ====================
    getRegionalPerformance: (f={}) => axios.get('qoc/regional-performance', {params:{...f, scoring_mode: getScoringMode()}}),
    getComplianceOverview(filters = {}) {
        return axios.get('qoc/analytics/overview', { params: {...filters, scoring_mode: getScoringMode()} });
    },
    getFacilityCompliance(facilityId, filters = {}) {
        return axios.get(`qoc/analytics/facility/${facilityId}`, { params: {...filters, scoring_mode: getScoringMode()} });
    },
    getStandardWiseScores(facilityId = null, assessmentId = null) {
        return axios.get('qoc/analytics/standards', {
            params: { facility_id: facilityId, assessment_id: assessmentId, scoring_mode: getScoringMode() },
        });
    },
    getTrendAnalysis(facilityId, filters = {}) {
        return axios.get(`qoc/analytics/trends/${facilityId}`, { params: {...filters, scoring_mode: getScoringMode()} });
    },
    getComparisonReport(filters = {}) {
        return axios.get('qoc/analytics/comparison', { params: {...filters, scoring_mode: getScoringMode()} });
    },
    getAggregatedStandardsPerformance(filters = {}) {
        return axios.get('qoc/analytics/aggregated-performance', { params: {...filters, scoring_mode: getScoringMode()} });
    },
    getPerformanceDistribution(filters = {}) {
        return axios.get('qoc/analytics/performance-distribution', { params: {...filters, scoring_mode: getScoringMode()} });
    },
    getQuickWins(filters = {}) {
        return axios.get('qoc/analytics/quick-wins', { params: {...filters, scoring_mode: getScoringMode()} });
    },
    getRegions() {
        return axios.get('qoc/analytics/regions');
    },
    getFacilityLevels() {
        return axios.get('qoc/analytics/facility-levels');
    },

    // ==================== COMPLIANCE ====================
    getCurrentCompliance(facilityId = null) {
        return axios.get('qoc/compliance/current', {
            params: { ...(facilityId ? { facility_id: facilityId } : {}), scoring_mode: getScoringMode() },
        });
    },
    getOverdueAssessments(facilityId = null) {
        return axios.get('qoc/compliance/overdue', {
            params: facilityId ? { facility_id: facilityId } : {},
        });
    },
    getActionItems(facilityId = null) {
        return axios.get('qoc/compliance/action-items', {
            params: facilityId ? { facility_id: facilityId } : {},
        });
    },
    getComplianceTrends(facilityId = null, months = 12) {
        return axios.get('qoc/compliance/trends', {
            params: { months, scoring_mode: getScoringMode(), ...(facilityId ? { facility_id: facilityId } : {}) },
        });
    },

    // ==================== KPIs ====================
    getKpis(filters = {}) {
        return axios.get('qoc/kpis', { params: filters });
    },
    saveKpi(data) {
        return axios.post('qoc/kpis/save', data);
    },
    deleteKpi(id) {
        return axios.post(`qoc/kpis/${id}/delete`);
    },
    getKpiVsActual(filters = {}) {
        return axios.get('qoc/kpis/vs-actual', { params: filters });
    },
    getPeriodConfig(period_label) {
        return axios.get('qoc/kpis/period-config', { params: { period_label } });
    },
    getKpiTargets(filters = {}) {
        return axios.get('qoc/kpi-targets', { params: filters });
    },
    saveKpiTarget(data) {
        return axios.post('qoc/kpi-targets/save', data);
    },

    // ==================== CUSTOMER SURVEYS ====================
    getCustomerSurveys(filters = {}) {
        return axios.get('qoc/surveys', { params: filters });
    },
    getSurveyAnalytics(filters = {}) {
        return axios.get('qoc/surveys/analytics', { params: filters });
    },
    getSurveyDetail(id) {
        return axios.get(`qoc/surveys/${id}`);
    },
    saveSurvey(data) {
        return axios.post('qoc/surveys/save', data);
    },
    deleteSurvey(id) {
        return axios.post(`qoc/surveys/${id}/delete`);
    },

    // ==================== QUALITY INDEX ====================
    getQualityIndex(filters = {}) {
        return axios.get('qoc/quality-index', { params: filters });
    },
    calculateQualityIndex(data) {
        return axios.post('qoc/quality-index/calculate', data);
    },

    // ==================== DASHBOARD BUILDER ====================
    getDashboards(includeWidgets = false) {
        return axios.get('qoc/dashboards', { params: { include_widgets: includeWidgets } });
    },
    getDashboardDetail(id, loadData = false) {
        return axios.get(`qoc/dashboards/${id}`, { params: { load_data: loadData } });
    },
    saveDashboard(data) {
        return axios.post('qoc/dashboards/save', data);
    },
    deleteDashboard(id) {
        return axios.post(`qoc/dashboards/${id}/delete`);
    },
    saveWidget(data) {
        return axios.post('qoc/widgets/save', data);
    },
    deleteWidget(id) {
        return axios.post(`qoc/widgets/${id}/delete`);
    },
    getWidgetData(widgetId, filters = {}) {
        return axios.get(`qoc/widgets/${widgetId}/data`, { params: filters });
    },

    // ==================== CAP ====================
    getCapItems(filters = {}) {
        return axios.get('qoc/cap', { params: filters });
    },
    saveCapItem(data) {
        return axios.post('qoc/cap/save', data);
    },
    deleteCapItem(id) {
        return axios.post(`qoc/cap/${id}/delete`);
    },
    verifyCapItem(id, data = {}) {
        return axios.post(`qoc/cap/${id}/verify`, data);
    },
    generateCapFromAssessment(assessmentId) {
        return axios.post(`qoc/cap/generate/${assessmentId}`);
    },
    getCapSummary(facilityId, periodLabel) {
        return axios.get('qoc/cap/summary', {
            params: { facility_id: facilityId, period_label: periodLabel },
        });
    },

    // ==================== AUDIT TEAM REGISTRY ====================
    getAuditTeams(filters = {}) {
        return axios.get('qoc/audit-teams', { params: filters });
    },
    saveAuditTeam(data) {
        return axios.post('qoc/audit-teams/save', data);
    },
    deleteAuditTeam(id) {
        return axios.post(`qoc/audit-teams/${id}/delete`);
    },

    // ==================== FACILITY PROFILE ====================
    getFacilityProfile(facilityId) {
        return axios.get(`qoc/facility-profile/${facilityId}`);
    },
    saveFacilityProfile(facilityId, data) {
        return axios.post(`qoc/facility-profile/${facilityId}/save`, data);
    },

    // ==================== FACILITY PROFILE DASHBOARD ====================
    getFacilityProfileCoverage(params) {
        return axios.get('qoc/facility-profile-coverage', { params });
    },
    getServicesAnalytics(params) {
        return axios.get('qoc/services-analytics', { params });
    },
    getStaffingAnalytics(params) {
        return axios.get('qoc/staffing-analytics', { params });
    },
    getSMTAlerts() {
        return axios.get('qoc/smt-alerts');
    },
    // ==================== EMAIL ====================
    sendEmail(data) {
        return axios.post('qoc/email/send', data);
    },

    // ==================== PERIOD CONFIG ====================
    savePeriodConfig(data) {
        return axios.post('qoc/period-config/save', data);
    },

    // Alias
    getAuditTeam(filters = {}) {
        return axios.get('qoc/audit-teams', { params: filters });
    },

    // ==================== FACILITY COORDINATES ====================
    saveFacilityCoordinates(facilityId, data) {
        return axios.post(`qoc/facilities/${facilityId}/coordinates`, data);
    },
    getFacilityQrData(facilityId) {
        return axios.get(`qoc/qr/generate/${facilityId}`);
    },
    pullCoordinatesFromMfl(params = {}) {
        return axios.post('qoc/facilities/pull-mfl-coordinates', params);
    },
    applyMflCoordinates(matched) {
        return axios.post('qoc/facilities/apply-mfl-coordinates', { matched });
    },

    // ==================== ASSESSMENT LOCKING ====================
    getAssessmentLock(id)    { return axios.get('qoc/assessments/' + id + '/lock2'); },
    setOfflineLock(id, d)    { return axios.post('qoc/assessments/' + id + '/offline-lock', d); },
    clearOfflineLock(id)     { return axios.post('qoc/assessments/' + id + '/offline-unlock'); },
    heartbeatAssessment(id)  { return axios.post('qoc/assessments/' + id + '/heartbeat2'); },
    clearOnlineEditing(id)   { return axios.post('qoc/assessments/' + id + '/clear-editing2'); },

    // ==================== HIE SYNC ====================
    pushAssessmentToHie(assessmentId) {
        return axios.post(`qoc/hie/push/${assessmentId}`);
    },
    pushPendingAssessments() {
        return axios.post('qoc/hie/push-pending');
    },
    getHieSyncStatus() {
        return axios.get('qoc/hie/status');
    },
    registerClauseCodes() {
        return axios.post('qoc/hie/register-codes');
    },
    getClauseChampions(params = {}) {
        return axios.get('/qoc/clause-champions', { params });
    },
 
    getOverallChampion(params = {}) {
        return axios.get('/qoc/overall-champion', { params });
    },
    getOverallChampionByQI(params = {}) {
        return axios.get('/qoc/overall-champion-qi', { params });
    },

// ==================== IPCAF ====================
getIpcafSections() {
    return axios.get('qoc/ipcaf/sections');
},
getIpcafQuestions(sectionId) {
    return axios.get('qoc/ipcaf/questions', { params: sectionId ? { section_id: sectionId } : {} });
},
getIpcafAssessments(filters = {}) {
    return axios.get('qoc/ipcaf/assessments', { params: filters });
},
getIpcafAssessmentResponses(assessmentId) {
    return axios.get(`qoc/ipcaf/assessments/${assessmentId}/responses`);
},
saveIpcafAssessment(data) {
    return axios.post('qoc/ipcaf/assessments/save', data);
},
saveIpcafResponses(assessmentId, responses) {
    return axios.post(`qoc/ipcaf/assessments/${assessmentId}/responses/save`, { responses });
},
submitIpcafAssessment(assessmentId) {
    return axios.post(`qoc/ipcaf/assessments/${assessmentId}/submit`);
},
approveIpcafAssessment(assessmentId) {
    return axios.post(`qoc/ipcaf/assessments/${assessmentId}/approve`);
},
requestIpcafRevision(assessmentId, notes) {
    return axios.post(`qoc/ipcaf/assessments/${assessmentId}/request-revision`, { notes });
},

// ==================== IPCAF locking ====================
getIpcafLockStatus(id)       { return axios.get('qoc/ipcaf/assessments/' + id + '/lock2'); },
setIpcafOfflineLock(id, d)   { return axios.post('qoc/ipcaf/assessments/' + id + '/offline-lock', d); },
clearIpcafOfflineLock(id)    { return axios.post('qoc/ipcaf/assessments/' + id + '/offline-unlock'); },
heartbeatIpcafAssessment(id) { return axios.post('qoc/ipcaf/assessments/' + id + '/heartbeat2'); },
clearIpcafOnlineEditing(id)  { return axios.post('qoc/ipcaf/assessments/' + id + '/clear-editing2'); },

// ==================== IPCAF analytics ====================
getIpcafWeakestIndicators()      { return axios.get('qoc/ipcaf/analytics/weakest-indicators'); },
getIpcafFacilityScorecard(facilityId) { return axios.get('qoc/ipcaf/analytics/facility-scorecard', { params: { facility_id: facilityId } }); },
getIpcafNationalSummary()        { return axios.get('qoc/ipcaf/analytics/national-summary'); },
    getMeChecklistItems(meId) {
        return axios.get(`qoc/measurable-elements/${meId}/checklist`);
    },
    saveMeChecklist(meId, data) {
        return axios.post(`qoc/measurable-elements/${meId}/checklist/save`, data);
    },

// ==================== CAP facility preview ====================
getCapFacilityPreview(facilityId, assessmentId) {
    return axios.get('qoc/cap/facility-preview', { params: { facility_id: facilityId, assessment_id: assessmentId } });
},
};

export default QoCService;


